import React, { useState, useMemo, useContext } from "react";
import { WidgetContext } from "../../store/widgetContext";
import { get } from "lodash";
import { Card, CardContent, Typography, Stack, Box } from "@mui/material";
import { KeyboardArrowRight as ArrowRightIcon } from "@mui/icons-material";
import SearchAndFaqs from "./SearchAndFaqs";
import HomeArticles from "./HomeArticles";
import StartActions from "./StartActions";
import { formatTimeAgo, trimString } from "../../utils/common";
import AvatarStack from "../AvatarStack";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";

export default function Home({ handleNavigate, setSelectedDocument, setSelectedCollection, isTextChatEnabled, isVoiceChatEnabled }) {
	const { widget } = useContext(WidgetContext);
	const [searchQuery, setSearchQuery] = useState("");
	const isFaqsEnabled = get(widget, "pages.faqs.enabled", false);
	const { msg } = useWidgetMsg();
	const filteredFaqs = useMemo(() => {
		const faqs = get(widget, "pages.faqs.docs", []);
		if (!searchQuery.trim()) {
			return faqs.slice(0, 3);
		}
		return faqs.filter((item) => item.doc?.title?.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 3);
	}, [widget, searchQuery]);

	const handleFaqClick = (faq, event) => {
		if (event) {
			event.stopPropagation();
		}
		if (faq?.doc && setSelectedCollection && handleNavigate) {
			setSelectedCollection(faq.doc);
			handleNavigate({ location: "collection" });
		}
	};

	const { conversations, agents } = useContext(WidgetContext);


	const latestConversation = useMemo(() => {
		if (!Array.isArray(conversations) || !Array.isArray(agents)) {
			return null;
		}

		const latest = conversations
			.filter((conv) => conv.placeholder !== true)
			.sort((a, b) => {
				const lastUpdatedA = new Date(
					a.records[a.records.length - 1]?.updatedAt || a.createdAt
				);
				const lastUpdatedB = new Date(
					b.records[b.records.length - 1]?.updatedAt || b.createdAt
				);
				return lastUpdatedB - lastUpdatedA;
			})[0];

		if (!latest) return null;

		const lastReplyRecord = [...latest.records].reverse().find(
			(record) => record?.reply
		);

		let lastMessage = msg("conversations.no_reply", "No reply yet");
		if (lastReplyRecord?.reply) {
			const reply = lastReplyRecord.reply;
			if (reply.type === "image") {
				lastMessage = "Image";
			} else if (reply.message) {
				lastMessage = reply.message;
			} else if (reply.config?.message) {
				lastMessage = reply.config.message;
			}
		}

		const assignedAgents = [];
		latest.assignees?.forEach((agentIDaasId) => {
			const agent = agents.find((el) => el.agentIDaasId === agentIDaasId);
			if (agent) {
				assignedAgents.push(agent);
			}
		});

		return {
			conversationId: latest._id,
			status: latest.status,
			agents: assignedAgents,
			lastMsg: trimString(lastMessage, 30),
			time: formatTimeAgo(lastReplyRecord?.updatedAt || latest.createdAt, msg),
		};
	}, [conversations, agents, msg]);

	const handleRecentMessageClick = (event) => {
		if (event) {
			event.stopPropagation();
		}
		if (latestConversation?.conversationId) {
			if (handleNavigate) {
				handleNavigate({ 
					location: "newConversation",
					conversationId: latestConversation.conversationId,
					assignedAgents: latestConversation.agents,
				});
			}
		} else if (handleNavigate) {
			handleNavigate({ location: "newConversation" });
		}
	};

	return (
		<Box
			sx={{
				flex: 1,
				overflowY: "auto",
				overflowX: "hidden",
				padding: "5px 10px 80px",
				display: "flex",
				flexDirection: "column",
				gap: "20px",
				paddingTop: "0px"
			}}
		>
			<Card
				sx={{
					borderRadius: "16px",
					boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
					marginTop: "5px",
					flexShrink: 0,
				}}
			>
				<CardContent sx={{ padding: "20px !important" }}>
					<Typography sx={{ fontSize: "14px", fontWeight: 500, mb: 2 }}>
						{msg("conversations.recent_message", "Recent message")}
					</Typography>
					{latestConversation ? (
						<Box sx={{ cursor: "pointer", mb: 2 }} onClick={(e) => handleRecentMessageClick(e)}>
							<Stack direction="row" spacing={1.5} alignItems="center">
								<AvatarStack agents={latestConversation?.agents || []} displayAgentInfo={false} />
								<Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
									<Typography 
										variant="body2" 
										sx={{ 
											fontSize: "12px",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{latestConversation?.lastMsg}
									</Typography>
									<Typography 
										sx={{ 
											fontSize: "8px", 
											color: "#808080",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{latestConversation?.time || msg("time.just_now", "Just now")}
									</Typography>
								</Box>
								<ArrowRightIcon sx={{ fontSize: 16, flexShrink: 0 }} />
							</Stack>
						</Box>
					) : (
						<Typography sx={{ fontSize: "14px", color: "#808080", textAlign: "center", mb: 2 }}>
							{msg("conversations.no_recent_messages", "No Recent Messages")}
						</Typography>
					)}
					{(isTextChatEnabled || isVoiceChatEnabled) && (
						<Box sx={{ mt: latestConversation ? 0 : 0 }}>
							<StartActions
								variant="solid"
								onStartChat={() => {
									const isActive = latestConversation?.conversationId && latestConversation.status === "Open";
									if (isActive) {
										handleNavigate({
											location: "newConversation",
											conversationId: latestConversation.conversationId,
											assignedAgents: latestConversation.agents,
										});
									} else {
										handleNavigate({ location: "newConversation" });
									}
								}}
								onStartCall={() => handleNavigate({ location: "newVoiceChat" })}
								showChat={isTextChatEnabled}
								showCall={isVoiceChatEnabled}
								startChatLabel={msg("home.start_new_chat", "Start a new chat")}
								startCallLabel={msg("home.start_new_call", "Start a new call")}
							/>
						</Box>
					)}
				</CardContent>
			</Card>

			{isFaqsEnabled && (
				<SearchAndFaqs
					searchQuery={searchQuery}
					onSearchChange={(e) => setSearchQuery(e.target.value)}
					filteredFaqs={filteredFaqs}
					onFaqClick={(faq, e) => handleFaqClick(faq, e)}
					widget={widget}
				/>
			)}

			<HomeArticles
				onArticleClick={(doc) => {
					setSelectedDocument(doc);
					handleNavigate({ location: "document" });
				}}
			/>
		</Box>
	);
}
